import React, { useState } from 'react';
import { Alert, Badge, Button, Col, Form, InputGroup, Row, Spinner, Table, ToggleButton, ToggleButtonGroup } from 'react-bootstrap';
import { GoTrash } from 'react-icons/go';
import {
  SERIES_HORIZON_MONTHS, SERIES_MAX_SESSIONS, SERIES_MIN_SESSIONS, WEEK_DAYS,
  formatDateBR, isValidDate, moveStart, shortWeekDay,
} from '../../utilities/agendamentoSeries';
import { useMediaQuery } from './useMediaQuery';

const INTERVALS = [1, 2, 3, 4];

// A tabela cabe no modal grande (800 px), que o Bootstrap usa a partir de 992 px; abaixo disso, a lista vira cards
const WIDE_LAYOUT = '(min-width: 992px)';

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`;

// Seção "Repetir em outros dias" do modal de agendamento (só em agendamento novo)
const RepeatAgendamento = ({ series, alert, onCloseAlert }) => {
  const [extraDate, setExtraDate] = useState('');
  const wide = useMediaQuery(WIDE_LAYOUT, true); // sem matchMedia, a tabela

  const { sessions, marks, check } = series;

  function addExtraDate() {
    if (!isValidDate(extraDate)) return;
    series.addExtraDate(extraDate);
    setExtraDate('');
  }

  // Guarda o que foi digitado e, ao sair do campo, prende entre 2 e 52
  function clampSessionsCount() {
    const value = Math.min(Math.max(Number(series.sessionsCount) || SERIES_MIN_SESSIONS, SERIES_MIN_SESSIONS), SERIES_MAX_SESSIONS);
    series.setSessionsCount(value);
  }

  // Mudar o início move o fim junto, mantendo a duração; se passar da meia-noite, o fim fica vazio
  function changeStart(session, value) {
    series.adjust(session.key, { start_time: value, end_time: moveStart(session, value) ?? '' });
  }

  function changeEnd(session, value) {
    series.adjust(session.key, { start_time: session.start_time, end_time: value });
  }

  // Cor da linha ou do card: erro, já agendado ou aviso
  function tone(session) {
    if (series.mainMark) return undefined;

    const mark = marks[session.key];
    const found = check.byKey[session.key];

    if (found?.reason === 'ALREADY_SCHEDULED') return 'info';
    if (mark?.type === 'error' || found?.blocking) return 'danger';
    if (mark?.type === 'warning' || found?.reason === 'PACIENTE_SAME_DAY') return 'warning';
    return undefined;
  }

  // Situação da sessão: o selo é sempre escrito, nunca só a cor. Os textos do servidor vão como texto, nunca como HTML
  function situationOf(session) {
    if (series.mainMark) return { label: '—' };

    const mark = marks[session.key];
    const found = check.byKey[session.key];

    if (mark?.type === 'error') return { bg: 'danger', label: mark.label, detail: mark.detail };
    if (found?.reason === 'SCHEDULE_CONFLICT') return { bg: 'danger', label: 'Ocupado', detail: found.message };
    if (found?.reason === 'ALREADY_SCHEDULED') return { bg: 'info', text: 'dark', label: 'Já agendado', detail: found.message };
    if (found?.reason === 'PACIENTE_SAME_DAY') return { bg: 'warning', text: 'dark', label: 'Aviso', detail: found.message };
    if (found?.reason === 'OVERLAP_IN_BATCH') return { bg: 'danger', label: 'Repetida', detail: found.message };
    if (found?.reason === 'INVALID_TIME') return { bg: 'danger', label: 'Horário inválido', detail: found.message };
    if (found?.reason === 'OUTSIDE_SERVICE_HOURS') return { bg: 'danger', label: 'Fora do horário', detail: found.message };
    if (found?.blocking) return { bg: 'danger', label: 'Recusada', detail: found.message };
    if (check.state === 'checking') return { label: 'Verificando…' };
    if (check.state === 'ready') return { bg: 'success', label: 'Livre' };
    if (check.state === 'waiting' || check.state === 'failed') return { bg: 'secondary', label: 'Não conferido' };
    return { label: '—' };
  }

  function badge(situation) {
    return situation.bg
      ? <Badge bg={situation.bg} text={situation.text}>{situation.label}</Badge>
      : <small className='text-muted'>{situation.label}</small>;
  }

  function warningBadge(session) {
    const mark = marks[session.key];
    return !series.mainMark && mark?.type === 'warning' ? <Badge bg='warning' text='dark' className='ms-1'>{mark.label}</Badge> : '';
  }

  function resetTimeLink(session) {
    return session.ownTime ? (
      <Button variant='link' size='sm' className='p-0 ms-1 align-baseline' onClick={() => series.resetTime(session.key)}>
        usar o horário de cima
      </Button>
    ) : '';
  }

  // Lixeira: na linha gerada pula a data (a série repõe no fim); na avulsa, remove
  function removeButton(session, withText) {
    return (
      <Button
        variant='outline-danger'
        size='sm'
        className={withText ? 'd-flex align-items-center gap-1 text-nowrap' : 'm-0 p-0 px-2'}
        onClick={() => series.remove(session.key)}
        aria-label={session.extra ? `Remover a data avulsa de ${formatDateBR(session.date)}` : `Pular a sessão de ${formatDateBR(session.date)}`}
        title={session.extra ? 'Remover a data' : 'Pular a data'}
      >
        <GoTrash />{withText ? (session.extra ? 'Remover' : 'Pular') : ''}
      </Button>
    );
  }

  function checkHeader() {
    if (series.mainMark)
      return <span className='text-danger'>{`${series.mainMark.detail ?? `${series.mainMark.label}.`} Corrija nos campos acima.`}</span>;

    if (check.state === 'checking')
      return <span className='text-muted'><Spinner animation='border' size='sm' /> Verificando…</span>;

    if (check.state === 'ready')
      return (
        <>
          {series.freeCount ? <Badge bg='success'>{plural(series.freeCount, 'livre', 'livres')}</Badge> : ''}
          {series.occupiedCount ? <Badge bg='danger'>{plural(series.occupiedCount, 'ocupada', 'ocupadas')}</Badge> : ''}
          {series.warningCount ? <Badge bg='warning' text='dark'>{series.warningCount} com aviso</Badge> : ''}
          {series.skippableCount ? (
            <Button variant='link' size='sm' className='p-0' onClick={series.skipOccupied}>
              Pular as datas ocupadas ({series.skippableCount})
            </Button>
          ) : ''}
        </>
      );

    if (check.message)
      return <span className={check.state === 'failed' ? 'text-danger' : 'text-muted'}>{check.message}</span>;

    return '';
  }

  function listSummary() {
    const ordered = sessions.map((session) => session.date).filter(isValidDate).sort();
    let text = plural(sessions.length, 'sessão', 'sessões');

    if (ordered.length > 0) text += ` · ${formatDateBR(ordered[0])} a ${formatDateBR(ordered[ordered.length - 1])}`;
    if (series.stoppedAtHorizon) text += ` · a série parou em ${SERIES_HORIZON_MONTHS} meses: ${series.generatedCount} de ${series.requested}`;

    return text;
  }

  // Computador: tabela com cabeçalho fixo e rolagem própria
  function sessionsTable() {
    return (
      <div style={{ maxHeight: '45vh', overflow: 'auto' }}>
        <Table size='sm' bordered className='mb-0 align-middle'>
          <thead>
            <tr>
              <th className='text-center'>Nº</th>
              <th>Data</th>
              <th>Dia</th>
              <th>Início</th>
              <th>Fim</th>
              <th>Situação</th>
              <th><span className='visually-hidden'>Ações</span></th>
            </tr>
          </thead>
          <tbody>
            {sessions.map((session, index) => {
              const situation = situationOf(session);

              return (
                <tr key={session.key} className={tone(session) ? `table-${tone(session)}` : undefined}>
                  <td className='text-center'>{index + 1}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    {session.main ? (
                      <>{formatDateBR(session.date)} <small className='text-muted'>data acima</small></>
                    ) : (
                      <Form.Control
                        size='sm'
                        type='date'
                        value={session.date}
                        onChange={(e) => series.adjust(session.key, { date: e.target.value })}
                        aria-label={`Data da sessão ${index + 1}`}
                        style={{ minWidth: 130 }}
                      />
                    )}
                  </td>
                  <td>{shortWeekDay(session.date)}</td>
                  <td>
                    {session.main ? session.start_time : (
                      <Form.Control size='sm' type='time' value={session.start_time} onChange={(e) => changeStart(session, e.target.value)} aria-label={`Início da sessão ${index + 1}`} />
                    )}
                  </td>
                  <td>
                    {session.main ? session.end_time : (
                      <Form.Control size='sm' type='time' value={session.end_time} onChange={(e) => changeEnd(session, e.target.value)} aria-label={`Fim da sessão ${index + 1}`} />
                    )}
                  </td>
                  <td>
                    {badge(situation)}
                    {situation.detail ? <small className='ms-1'>{situation.detail}</small> : ''}
                    {warningBadge(session)}
                    {resetTimeLink(session)}
                  </td>
                  <td className='text-center'>{session.main ? '' : removeButton(session, false)}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>
    );
  }

  // Celular e tablet: um card por sessão, sem rolagem lateral
  function sessionsCards() {
    return (
      <div className='d-grid gap-2'>
        {sessions.map((session, index) => {
          const situation = situationOf(session);
          const color = tone(session);

          return (
            <div key={session.key} className={`border rounded p-2 ${color ? `border-${color}-subtle bg-${color}-subtle` : ''}`}>
              <div className='d-flex align-items-center gap-2'>
                <b>{index + 1}.</b>
                <span>{shortWeekDay(session.date)}{session.main ? ` ${formatDateBR(session.date)}` : ''}</span>
                <span className='d-flex flex-wrap align-items-center'>{badge(situation)}{warningBadge(session)}</span>
                <span className='ms-auto'>
                  {session.main ? <small className='text-muted'>data acima</small> : removeButton(session, true)}
                </span>
              </div>

              {session.main ? (
                <div className='small mt-1'>{session.start_time} – {session.end_time}</div>
              ) : (
                <Row className='g-2 mt-0'>
                  <Col xs={12}>
                    <InputGroup size='sm'>
                      <InputGroup.Text>Data</InputGroup.Text>
                      <Form.Control type='date' value={session.date} onChange={(e) => series.adjust(session.key, { date: e.target.value })} aria-label={`Data da sessão ${index + 1}`} />
                    </InputGroup>
                  </Col>
                  <Col xs={6}>
                    <InputGroup size='sm'>
                      <InputGroup.Text>Início</InputGroup.Text>
                      <Form.Control type='time' value={session.start_time} onChange={(e) => changeStart(session, e.target.value)} aria-label={`Início da sessão ${index + 1}`} />
                    </InputGroup>
                  </Col>
                  <Col xs={6}>
                    <InputGroup size='sm'>
                      <InputGroup.Text>Fim</InputGroup.Text>
                      <Form.Control type='time' value={session.end_time} onChange={(e) => changeEnd(session, e.target.value)} aria-label={`Fim da sessão ${index + 1}`} />
                    </InputGroup>
                  </Col>
                </Row>
              )}

              {situation.detail || session.ownTime ? (
                <div className='small mt-1'>{situation.detail}{resetTimeLink(session)}</div>
              ) : ''}
            </div>
          );
        })}
      </div>
    );
  }

  return (
    <div className='mt-2'>
      <hr className='my-2' />

      <Form.Check
        type='switch'
        id='repeat-agendamento'
        label='Repetir em outros dias'
        checked={series.enabled}
        onChange={(e) => series.enable(e.target.checked)}
      />

      {!series.enabled ? (
        <small className='text-muted'>Crie as próximas sessões de uma vez, com o mesmo paciente, doutor, tipo, prontuário, status e horário.</small>
      ) : (
        <div className='d-grid gap-2 mt-2'>
          <Row className='g-2 align-items-end'>
            <Col xs={12} md='auto'>
              <div id='repeat-days-label' className='form-label mb-1'>Dias da semana</div>
              <ToggleButtonGroup
                type='checkbox'
                value={series.weekDays}
                onChange={(values) => series.setWeekDays(values)}
                className='d-flex flex-wrap gap-1'
                aria-labelledby='repeat-days-label'
              >
                {WEEK_DAYS.map((day) => (
                  <ToggleButton key={day.value} id={`repeat-day-${day.value}`} value={day.value} variant='outline-primary' size='sm' aria-label={day.name}>
                    {day.short}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Col>
            <Col xs={6} md='auto'>
              <Form.Label htmlFor='repeat-interval' className='mb-1'>Repetir a cada</Form.Label>
              <Form.Select id='repeat-interval' size='sm' value={series.intervalWeeks} onChange={(e) => series.setIntervalWeeks(Number(e.target.value))}>
                {INTERVALS.map((value) => (
                  <option key={value} value={value}>{value} {value === 1 ? 'semana' : 'semanas'}</option>
                ))}
              </Form.Select>
            </Col>
            <Col xs={6} md='auto'>
              <Form.Label htmlFor='repeat-sessions' className='mb-1'>Sessões</Form.Label>
              <Form.Control
                id='repeat-sessions'
                size='sm'
                type='number'
                min={SERIES_MIN_SESSIONS}
                max={SERIES_MAX_SESSIONS}
                value={series.sessionsCount}
                onChange={(e) => series.setSessionsCount(e.target.value)}
                onBlur={clampSessionsCount}
                style={{ maxWidth: 100 }}
              />
            </Col>
          </Row>
          <small className='text-muted'>Inclui a 1ª sessão (a data acima). Máximo de {SERIES_MAX_SESSIONS} sessões em até {SERIES_HORIZON_MONTHS} meses. Datas adicionadas à mão somam a este número.</small>

          {alert ? (
            <Alert variant={alert.variant} dismissible onClose={onCloseAlert} className='mb-0 py-2'>
              <b>{alert.title}</b> {alert.text}
            </Alert>
          ) : ''}

          <div className='d-flex flex-wrap justify-content-between align-items-center gap-2'>
            <span>
              <b>Sessões que serão criadas</b>{' '}
              <small className='text-muted'>{listSummary()}</small>
            </span>
            <small className='d-flex flex-wrap align-items-center gap-1' aria-live='polite'>{checkHeader()}</small>
          </div>

          {sessions.length === 0 ? (
            <small className='text-muted'>Informe a data da 1ª sessão nos campos acima.</small>
          ) : (wide ? sessionsTable() : sessionsCards())}

          <div className='d-flex flex-wrap align-items-center gap-2'>
            <Form.Label htmlFor='repeat-extra-date' className='mb-0'>Adicionar data</Form.Label>
            <Form.Control
              id='repeat-extra-date'
              size='sm'
              type='date'
              value={extraDate}
              onChange={(e) => setExtraDate(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  addExtraDate();
                }
              }}
              style={{ maxWidth: 160 }}
            />
            <Button variant='outline-primary' size='sm' onClick={addExtraDate} disabled={!isValidDate(extraDate)}>+ Adicionar</Button>
          </div>

          {series.skippedInRule.length > 0 ? (
            <div className='d-flex flex-wrap align-items-center gap-2'>
              <small className='text-muted'>Datas puladas:</small>
              {series.skippedInRule.map((date) => (
                <Button key={date} variant='outline-secondary' size='sm' className='py-0' onClick={() => series.undoSkip(date)} aria-label={`Desfazer o pulo de ${formatDateBR(date)}`}>
                  {formatDateBR(date)} · desfazer
                </Button>
              ))}
            </div>
          ) : ''}
        </div>
      )}
    </div>
  );
};

export default RepeatAgendamento;
